import { formatHumanName } from '@medplum/core';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import type {
  Appointment,
  Condition,
  Coverage,
  EpisodeOfCare,
  Patient,
  Practitioner,
} from '@medplum/fhirtypes';
import { getPrimaryActiveCoverage } from '@unify/fhir-model/resources/coverage';
import { displayLabelForEpisodeType } from '@/lib/fhirValueSets/sessionAndEpisodeTypes';
import { getApptEocContextRef } from '@unify/fhir-model/resources/appointment';
import { resolveAppointmentModality } from '@/pages/appointments/utils/appointmentModality';
import { resolveParticipantAttendanceFromAppointmentParticipant } from '@/pages/appointments/utils/participantAttendance';
import type { TreatmentPlan } from '../../clients/data/types';
import type {
  GroupParticipant,
  SessionAppointment,
  SessionClient,
} from '../data/mockSessionData';

function patientInitials(patient: Patient): string {
  const given = patient.name?.[0]?.given?.[0]?.[0] ?? '';
  const family = patient.name?.[0]?.family?.[0] ?? '';
  return `${given}${family}`.toUpperCase() || '?';
}

function ageFromBirthDate(birthDate: string | undefined): number {
  if (!birthDate) return 0;
  const born = new Date(birthDate);
  if (Number.isNaN(born.getTime())) return 0;
  const today = new Date();
  let age = today.getFullYear() - born.getFullYear();
  const m = today.getMonth() - born.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age -= 1;
  return age;
}

function mapConditionsToDiagnoses(conditions: Condition[]): {
  primaryDiagnosis: SessionClient['primaryDiagnosis'];
  secondaryDiagnoses?: SessionClient['secondaryDiagnoses'];
} {
  const active = conditions.filter((c) =>
    c.clinicalStatus?.coding?.some((coding) => coding.code === 'active'),
  );
  const primary = active[0];
  const primaryCode = primary?.code?.coding?.[0]?.code ?? primary?.code?.text ?? '—';
  const primaryDesc =
    primary?.code?.coding?.[0]?.display ?? primary?.code?.text ?? 'Active diagnosis not recorded';
  const secondary = active.slice(1).map((c) => ({
    code: c.code?.coding?.[0]?.code ?? c.code?.text ?? '—',
    description: c.code?.coding?.[0]?.display ?? c.code?.text ?? 'Diagnosis',
  }));
  return {
    primaryDiagnosis: { code: primaryCode, description: primaryDesc },
    secondaryDiagnoses: secondary.length > 0 ? secondary : undefined,
  };
}

function mapEpisodeDiagnosesToSession(
  episodeOfCare: EpisodeOfCare | null | undefined,
  conditions: Condition[],
): ReturnType<typeof mapConditionsToDiagnoses> | null {
  if (!episodeOfCare?.diagnosis?.length) return null;

  const ranked = [...episodeOfCare.diagnosis].sort(
    (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER),
  );
  const episodeConditions: Condition[] = [];
  for (const entry of ranked) {
    const conditionId = entry.condition?.reference?.replace(/^Condition\//, '');
    if (!conditionId) continue;
    const condition = conditions.find((candidate) => candidate.id === conditionId);
    if (condition) episodeConditions.push(condition);
  }

  if (episodeConditions.length === 0) return null;
  return mapConditionsToDiagnoses(episodeConditions);
}

function mapCoverageToInsurance(coverages: Coverage[]): SessionClient['insurance'] {
  const primary = getPrimaryActiveCoverage(coverages);
  if (!primary) {
    return { payer: '—', memberId: '—', plan: '—' };
  }
  const payer = primary.payor?.[0]?.display ?? 'Insurance on file';
  const memberId =
    primary.subscriberId ??
    primary.identifier?.find((id) => id.type?.coding?.[0]?.code === 'MB')?.value ??
    '—';
  const plan =
    primary.class?.find((cl) => cl.type?.coding?.[0]?.code === 'plan')?.name ??
    primary.class?.[0]?.name ??
    '—';
  return { payer, memberId, plan };
}

export function mapPatientToSessionClient(
  patient: Patient,
  conditions: Condition[],
  coverages: Coverage[],
  episodeOfCare: EpisodeOfCare | null | undefined,
  practitioner: Practitioner | undefined,
  treatmentPlan?: TreatmentPlan | null,
  emergencyContactOverride?: SessionClient['emergencyContact'],
): SessionClient {
  const fromEpisode = mapEpisodeDiagnosesToSession(episodeOfCare, conditions);
  const fromPlan =
    treatmentPlan?.diagnosisCode != null && treatmentPlan.diagnosisCode !== ''
      ? {
          primaryDiagnosis: {
            code: treatmentPlan.diagnosisCode,
            description:
              treatmentPlan.diagnosisDescription ??
              treatmentPlan.diagnosis ??
              'Diagnosis on treatment plan',
          },
          secondaryDiagnoses: undefined as SessionClient['secondaryDiagnoses'],
        }
      : null;
  const dx = fromPlan ?? fromEpisode ?? mapConditionsToDiagnoses(conditions);
  const contact = patient.contact?.[0];
  const emergencyFromPatient = {
    name:
      contact?.name?.text ??
      (Array.isArray(contact?.name) ? formatHumanName(contact.name[0]) : formatHumanName(contact?.name)) ??
      '—',
    relationship: contact?.relationship?.[0]?.text ?? contact?.relationship?.[0]?.coding?.[0]?.display ?? '—',
    phone: contact?.telecom?.find((t) => t.system === 'phone')?.value ?? '—',
  };
  const eocLabel = displayLabelForEpisodeType(episodeOfCare?.type?.[0]);

  const alerts: SessionClient['alerts'] = [];
  const siCondition = conditions.find((c) =>
    (c.code?.coding?.[0]?.code ?? '').startsWith('R45') ||
    (c.code?.text ?? '').toLowerCase().includes('suicid'),
  );
  if (siCondition) {
    alerts.push({
      level: 'warning',
      message: `Risk-related diagnosis on chart: ${siCondition.code?.coding?.[0]?.display ?? siCondition.code?.text}`,
    });
  }

  return {
    id: patient.id ?? '',
    name: formatHumanName(patient.name?.[0]) || 'Client',
    initials: patientInitials(patient),
    pronouns:
      patient.extension?.find((e) => e.url === 'https://unify.health/fhir/pronouns')?.valueString ??
      '—',
    age: ageFromBirthDate(patient.birthDate),
    dob: patient.birthDate ?? '—',
    primaryDiagnosis: dx.primaryDiagnosis,
    secondaryDiagnoses: dx.secondaryDiagnoses,
    insurance: mapCoverageToInsurance(coverages),
    emergencyContact: emergencyContactOverride ?? emergencyFromPatient,
    alerts,
    episodeStatus: eocLabel,
    provider: practitioner ? formatPractitionerStaffLabel(practitioner) || 'Provider' : '—',
  };
}

export function mapAppointmentToSessionAppointment(
  appointment: Appointment,
  client: SessionClient,
  groupRoster?: GroupParticipant[],
): SessionAppointment {
  const patientCount = (appointment.participant ?? []).filter((p) =>
    p.actor?.reference?.startsWith('Patient/'),
  ).length;
  const modality = resolveAppointmentModality(appointment);
  const start = appointment.start ?? new Date().toISOString();
  const end = appointment.end;
  let duration = appointment.minutesDuration ?? 50;
  if (end && start) {
    const ms = Date.parse(end) - Date.parse(start);
    if (Number.isFinite(ms) && ms > 0) duration = Math.round(ms / 60_000);
  }

  return {
    id: appointment.id ?? '',
    sessionType: patientCount > 1 ? 'group' : 'individual',
    scheduledTime: start,
    duration,
    modality: modality === 'telehealth' ? 'telehealth' : 'telehealth',
    client,
    groupRoster,
  };
}

export function resolveEpisodeIdFromAppointment(appointment: Appointment): string | undefined {
  const ref = getApptEocContextRef(appointment)?.reference;
  if (!ref?.startsWith('EpisodeOfCare/')) return undefined;
  return ref.replace('EpisodeOfCare/', '');
}

export function buildGroupRosterFromAppointment(
  appointment: Appointment,
  primaryPatientId: string,
): GroupParticipant[] {
  return (appointment.participant ?? [])
    .filter((p) => p.actor?.reference?.startsWith('Patient/'))
    .map((p) => {
      const id = p.actor!.reference!.replace('Patient/', '');
      const name = p.actor?.display ?? id;
      const initials = name
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
      return {
        id,
        name,
        initials: initials || '?',
        status: resolveParticipantAttendanceFromAppointmentParticipant(p, appointment),
      };
    })
    .filter((r) => r.id !== primaryPatientId || (appointment.participant ?? []).filter((x) =>
      x.actor?.reference?.startsWith('Patient/'),
    ).length <= 1);
}
